/**
 * Fair Home V2 — TEMPORARY pager debug log.
 *
 * A tester's laptop still pages wrong (skips / jumps back) and nobody else can
 * reproduce it. This records every wheel event with the arbitration decision,
 * every slide change with its cause, and the slide-fit zooms, into a ring buffer
 * the tester downloads with the on-page button (or `window.fairPagerLog.download()`).
 *
 * Remove once the bug is found: this file, its imports in `FairHomeV2Page.vue`,
 * `useFairHomeV2SlideScroll.ts`, `useFairHomeV2SlideFit.ts`.
 */
import type { Swiper as SwiperInstance } from 'swiper/types'

/** ~1 min of continuous trackpad scrolling at 60 events/s. */
const MAX_EVENTS = 5000

type PagerDebugEvent = { t: number; k: string } & Record<string, unknown>

const startedAt = new Date().toISOString()
const events: PagerDebugEvent[] = []

function now(): number {
  return Math.round(performance.now())
}

function push(event: PagerDebugEvent) {
  events.push(event)
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS)
}

function pagerSwiper(): SwiperInstance | null {
  const el = document.querySelector('.fair-home-v2__pager') as
    | (HTMLElement & { swiper?: SwiperInstance })
    | null
  return el?.swiper ?? null
}

function shortTarget(target: EventTarget | null): string {
  const el = target as HTMLElement | null
  if (!el || typeof el.className !== 'string') return el?.tagName ?? '?'
  const cls = el.className.split(' ').find((c) => c.startsWith('fair-')) ?? el.className.split(' ')[0]
  return `${el.tagName.toLowerCase()}.${cls ?? ''}`
}

/** Wheel event as seen by `useFairHomeV2SlideScroll`, with what it decided. */
export function logPagerWheel(
  event: WheelEvent,
  decision: string,
  extra: Record<string, unknown> = {},
) {
  const swiper = pagerSwiper()
  push({
    t: Math.round(event.timeStamp),
    k: 'wheel',
    dy: Math.round(event.deltaY * 100) / 100,
    dx: Math.round(event.deltaX * 100) / 100,
    dm: event.deltaMode,
    ...(event.ctrlKey ? { ctrl: true } : {}),
    dec: decision,
    slide: swiper?.activeIndex,
    anim: swiper?.animating,
    tgt: shortTarget(event.target),
    ...extra,
  })
}

/** Slide-fit result for one slide (`useFairHomeV2SlideFit`). */
export function logPagerFit(inner: HTMLElement, data: Record<string, unknown>) {
  const section = inner.firstElementChild as HTMLElement | null
  push({ t: now(), k: 'fit', sec: shortTarget(section), ...data })
}

/** Slide changes and what caused them. */
/**
 * Swiper announces the cause (`scroll`, `keyPress`) only after `slideChange`, so
 * read it off the raw events that just happened instead.
 */
function guessSlideCause(dragged: boolean): string {
  const t = now()
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!
    if (t - e.t > 120) break
    if (e.k === 'wheel' && String(e.dec).startsWith('pass')) return 'wheel'
    if (e.k === 'key') return `key:${String(e.key)}`
    if (e.k === 'pointer-up') return dragged ? 'drag' : `click:${String(e.tgt ?? '')}`
  }
  return dragged ? 'drag' : 'programmatic/other'
}

export function attachPagerDebug(swiper: SwiperInstance) {
  let dragged = false

  swiper.on('touchStart', () => {
    dragged = false
    push({ t: now(), k: 'drag-start', slide: swiper.activeIndex })
  })
  swiper.on('sliderMove', () => {
    if (!dragged) push({ t: now(), k: 'drag-move', slide: swiper.activeIndex })
    dragged = true
  })
  swiper.on('touchEnd', () => push({ t: now(), k: 'drag-end', slide: swiper.activeIndex, dragged }))
  swiper.on('slideChange', () => {
    push({
      t: now(),
      k: 'slide',
      from: swiper.previousIndex,
      to: swiper.activeIndex,
      cause: guessSlideCause(dragged),
    })
    dragged = false
  })
  swiper.on('transitionEnd', () => push({ t: now(), k: 'slide-done', slide: swiper.activeIndex }))
  push({ t: now(), k: 'attach', slide: swiper.activeIndex })
}

/** Throttle per key, so drags and native scrolls stay readable but complete enough. */
const lastAt = new Map<string, number>()
function throttled(key: string, ms: number): boolean {
  const t = now()
  if (t - (lastAt.get(key) ?? -Infinity) < ms) return true
  lastAt.set(key, t)
  return false
}

/**
 * Everything besides the wheel that can move the page on a laptop: click/tap-drag
 * on the touchpad (pointer events — the pager pages on drag, `simulateTouch`),
 * native scrolling of slides and rows, focus jumps, keys, touch, Safari gestures.
 * Capture phase on `document`, passive — nothing here changes behaviour.
 */
function attachDocumentListeners() {
  const opts = { capture: true, passive: true } as const
  let pointerDown = false

  window.addEventListener('resize', () =>
    push({ t: now(), k: 'resize', w: window.innerWidth, h: window.innerHeight }),
  )
  document.addEventListener('visibilitychange', () =>
    push({ t: now(), k: 'visibility', state: document.visibilityState }),
  )

  document.addEventListener(
    'pointerdown',
    (e) => {
      pointerDown = true
      push({ t: Math.round(e.timeStamp), k: 'pointer-down', pt: e.pointerType, btn: e.button, x: Math.round(e.clientX), y: Math.round(e.clientY), tgt: shortTarget(e.target), slide: pagerSwiper()?.activeIndex })
    },
    opts,
  )
  document.addEventListener(
    'pointermove',
    (e) => {
      if (!pointerDown || throttled('pointer-move', 50)) return
      push({ t: Math.round(e.timeStamp), k: 'pointer-move', pt: e.pointerType, x: Math.round(e.clientX), y: Math.round(e.clientY) })
    },
    opts,
  )
  for (const type of ['pointerup', 'pointercancel'] as const) {
    document.addEventListener(
      type,
      (e) => {
        pointerDown = false
        push({ t: Math.round(e.timeStamp), k: type.replace('pointer', 'pointer-'), pt: e.pointerType, x: Math.round(e.clientX), y: Math.round(e.clientY), tgt: shortTarget(e.target), slide: pagerSwiper()?.activeIndex })
      },
      opts,
    )
  }

  for (const type of ['touchstart', 'touchend'] as const) {
    document.addEventListener(
      type,
      (e) => push({ t: Math.round(e.timeStamp), k: type, n: e.touches.length, tgt: shortTarget(e.target) }),
      opts,
    )
  }

  // Native scrolling of anything (slide scrollports, card rows, the pager itself).
  document.addEventListener(
    'scroll',
    (e) => {
      const el = e.target as HTMLElement | Document
      const tgt = el === document ? 'document' : shortTarget(el)
      if (throttled(`scroll:${tgt}`, 50)) return
      const box = el === document ? document.scrollingElement : (el as HTMLElement)
      push({ t: Math.round(e.timeStamp), k: 'scroll', tgt, top: Math.round(box?.scrollTop ?? 0), left: Math.round(box?.scrollLeft ?? 0) })
    },
    opts,
  )

  document.addEventListener(
    'focusin',
    (e) => push({ t: Math.round(e.timeStamp), k: 'focus', tgt: shortTarget(e.target) }),
    opts,
  )
  document.addEventListener(
    'keydown',
    (e) => push({ t: Math.round(e.timeStamp), k: 'key', key: e.key }),
    opts,
  )

  // Safari trackpad pinch / rotate.
  for (const type of ['gesturestart', 'gestureend']) {
    document.addEventListener(type, (e) => push({ t: Math.round(e.timeStamp), k: type }), opts)
  }
}

if (typeof window !== 'undefined') attachDocumentListeners()

function snapshot() {
  return {
    meta: {
      startedAt,
      savedAt: new Date().toISOString(),
      url: location.href,
      ua: navigator.userAgent,
      platform: navigator.platform,
      dpr: window.devicePixelRatio,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      screen: { w: screen.width, h: screen.height },
      reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
      slides: Array.from(document.querySelectorAll<HTMLElement>('.fair-home-v2__slide-inner')).map(
        (inner) => ({
          sec: shortTarget(inner.firstElementChild),
          zoom: (inner.firstElementChild as HTMLElement | null)?.style.getPropertyValue('zoom') || '1',
          overflow: inner.scrollHeight - inner.clientHeight,
          scrollTop: inner.scrollTop,
        }),
      ),
      eventCount: events.length,
    },
    events,
  }
}

export function downloadPagerLog() {
  const blob = new Blob([JSON.stringify(snapshot())], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `fair-pager-log-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    fairPagerLog: {
      snapshot,
      download: downloadPagerLog,
      clear: () => events.splice(0),
    },
  })
}
